import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { FaEdit, FaTrash, FaCheck, FaUndo, FaPlus, FaSave } from "react-icons/fa"
import { useState, useEffect } from "react"
import { WritingsService, WritingVersionsService } from "@/client"
import useCustomToast from "@/hooks/useCustomToast"

export function WritingVersionPanel({
  writingId,
  editor,
}: {
  writingId: string
  editor: any
}) {
  const { showSuccessToast, showErrorToast } = useCustomToast()
  const queryClient = useQueryClient()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editedLabel, setEditedLabel] = useState<string>("")
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null)
  const [versionSnapshot, setVersionSnapshot] = useState<any>(null)
  const [hasChangedFromVersion, setHasChangedFromVersion] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ["writingVersions", writingId],
    queryFn: () => WritingVersionsService.listWritingVersions({ writingId }),
  })

  const restoreVersion = async (versionId: string) => {
    try {
      const version = await WritingVersionsService.getWritingVersionContent({
        writingId,
        versionId,
      })
  
      editor.commands.setContent(version.content)
      setActiveVersionId(versionId)
      setVersionSnapshot(version.content)
      setHasChangedFromVersion(false)  // reset
  
      showSuccessToast("Version loaded into editor")
    } catch (err) {
      showErrorToast("Failed to load version")
    }
  }

  useEffect(() => {
    if (!editor) return
  
    const handleUpdate = () => {
      if (versionSnapshot) {
        const current = JSON.stringify(editor.getJSON())
        const original = JSON.stringify(versionSnapshot)
        setHasChangedFromVersion(current !== original)
      }
    }
  
    editor.on("update", handleUpdate)
    return () => editor.off("update", handleUpdate)
  }, [editor, versionSnapshot])
  
  const setAsCurrentMutation = useMutation({
    mutationFn: async (versionId: string) => {
      const version = await WritingVersionsService.getWritingVersionContent({
        writingId,
        versionId,
      })
  
      await WritingsService.updateWritingContent({
        id: writingId,
        requestBody: {
          content: version.content,
        },
      })
  
      editor.commands.setContent(version.content)
      return version.content
    },
    onSuccess: () => {
      showSuccessToast("현재 버전으로 설정되었습니다.")
      queryClient.invalidateQueries({ queryKey: ["writing", writingId] })

      // Reset selection and highlight
      setActiveVersionId(null)
      setVersionSnapshot(null)
      setHasChangedFromVersion(false)
    },
    onError: () => {
      showErrorToast("현재 버전으로 설정하지 못했습니다.")
    },
  })

  const createVersionMutation = useMutation({
    mutationFn: async () =>
      WritingVersionsService.createWritingVersion({
        writingId,
        requestBody: {
          label: `버전 - ${new Date().toLocaleString()}`,
        },
      }),
    onSuccess: () => {
      showSuccessToast("새로운 버전 생성")
      queryClient.invalidateQueries({ queryKey: ["writingVersions", writingId] })
    },
  })

  const updateVersionContentMutation = useMutation({
    mutationFn: async () =>
      WritingVersionsService.updateWritingVersionContent({
        writingId,
        versionId: activeVersionId!,
        requestBody: {
          content: editor.getJSON(),
        },
      }),
    onSuccess: () => {
      showSuccessToast("버전 수정되었습니다.")
      queryClient.invalidateQueries({ queryKey: ["writingVersions", writingId] })
      setVersionSnapshot(editor.getJSON()) // ✅ reset snapshot after saving
      setHasChangedFromVersion(false)
    }
  })

  const updateLabelMutation = useMutation({
    mutationFn: async ({ versionId, label }: { versionId: string; label: string }) =>
      WritingVersionsService.updateWritingVersionLabel({
        writingId,
        versionId,
        requestBody: { label },
      }),
    onSuccess: () => {
      showSuccessToast("버전 정보가 수정되었습니다.")
      setEditingId(null)
      queryClient.invalidateQueries({ queryKey: ["writingVersions", writingId] })
    },
  })

  const deleteVersionMutation = useMutation({
    mutationFn: async (versionId: string) =>
      WritingVersionsService.deleteWritingVersion({ writingId, versionId }),
    onSuccess: () => {
      showSuccessToast("버전이 삭제되었습니다.")
      queryClient.invalidateQueries({ queryKey: ["writingVersions", writingId] })
    },
  })

  return (
    <Box
      w="300px"
      p={4}
      // borderLeft="1px solid"
      borderColor="gray.200"
      bg="bg.option2"
      h="100%"
      overflowY="auto"
      position="sticky"
      top="0"
    >
      <Flex align="center" justify="space-between" mb={4}>
        <Heading size="sm">버전 관리</Heading>
        <Button
          size="sm"
          variant="ghost"
          colorScheme="blue"
          onClick={() => createVersionMutation.mutate()}
          disabled={!!activeVersionId}
        >
          <FaPlus />
          새로운 버전 생성
        </Button>
      </Flex>

      <VStack align="stretch" gap={2} mb={4}>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => updateVersionContentMutation.mutate()}
          disabled={!activeVersionId || !hasChangedFromVersion}
        >
          <FaSave />
          버전 수정
        </Button>
      </VStack>

      {isLoading ? (
        <Spinner size="sm" />
      ) : (
        <VStack align="start" gap={4}>
          {data?.data.map((version: any) => (
            <Box
              key={version.id}
              w="100%"
              p={2}
              borderRadius="md"
              border="1px solid"
              borderColor={version.id === activeVersionId ? "blue.300" : "gray.200"}
              bg={version.id === activeVersionId ? "blue.50" : "white"}
              onClick={() => restoreVersion(version.id)}
              cursor="pointer"
            >
              <Flex justify="space-between" align="center" gap={2}>
                {editingId === version.id ? (
                  <Input
                    size="sm"
                    value={editedLabel}
                    onChange={(e) => setEditedLabel(e.target.value)}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <Text fontSize="sm" fontWeight="medium" truncate>
                    {version.label || "Unnamed"}
                  </Text>
                )}
                {editingId !== version.id && (
                  <HStack gap={0} onClick={(e) => e.stopPropagation()}>
                    {version.id === activeVersionId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        colorScheme="green"
                        disabled={
                          JSON.stringify(editor.getJSON()) === JSON.stringify(versionSnapshot)
                        }
                        onClick={(e) => {
                          e.stopPropagation()
                          setAsCurrentMutation.mutate(version.id)
                        }}
                      >
                        <Icon as={FaCheck} fontSize="16px" mr={1} />
                        {/* Set as Current */}
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      colorScheme="blue"
                      onClick={() => {
                        setEditingId(version.id)
                        setEditedLabel(version.label || "")
                      }}
                    >
                      <Icon as={FaEdit} fontSize="16px" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      colorScheme="red"
                      onClick={() => deleteVersionMutation.mutate(version.id)}
                    >
                      <Icon as={FaTrash} fontSize="16px" />
                    </Button>
                  </HStack>
                )}

              </Flex>

              <Text fontSize="xs" color="gray.500" mt={1}>
                {new Date(version.created_at).toLocaleString()}
              </Text>

              {editingId === version.id && (
                <HStack gap={2} mt={2} onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="xs"
                    colorScheme="green"
                    variant="solid"
                    onClick={() =>
                      updateLabelMutation.mutate({
                        versionId: version.id,
                        label: editedLabel,
                      })
                    }
                  >
                    <Icon as={FaCheck} fontSize="12px" mr={1} />
                    저장
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => setEditingId(null)}
                  >
                    <Icon as={FaUndo} fontSize="12px" mr={1} />
                    취소
                  </Button>
                </HStack>
              )}
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  )
}
